const MAX_RANGE_SIZE = 500
// Open-ended ranges (`KA29+`, `PE38+`) expand up to this district number. UK postcode districts
// rarely exceed 99 in practice; the postcode cache will resolve the ones that exist and the rest
// will fall through to the prefix-centroid match.
const OPEN_RANGE_END = 99

const PREFIX_RANGE = /^([A-Z]+)(\d+)-(\d+)$/
const PREFIX_OPEN_RANGE = /^([A-Z]+)(\d+)\+$/
const PREFIX_SINGLE = /^([A-Z]+)(\d+)$/
const BARE_RANGE = /^(\d+)-(\d+)$/
const BARE_OPEN_RANGE = /^(\d+)\+$/
const BARE_NUMBER = /^(\d+)$/
const PREFIX_ONLY = /^([A-Z]+)$/

interface ParseResult {
  postcodes: string[]
  warnings: string[]
}

/**
 * Expands postcode range input into individual postcodes.
 *
 * Accepts comma, colon, or whitespace as token delimiters (mix freely). Tokens that share a prefix
 * with the most-recently-seen letter group inherit that prefix (carry-forward).
 *
 * Supported token shapes (after upper-casing and collapsing whitespace around hyphens):
 * - `AL` / `BA` — area prefix only (push as-is, becomes new carry)
 * - `KT19` / `S1` — single district (push, prefix becomes carry)
 * - `G1-62` — district range
 * - `64-82` / `87` — bare range or number, uses current carry
 * - `KA29+` / `PE38+` — open-ended range (KA29..KA99)
 * - `29+` — open-ended bare, uses current carry
 *
 * Examples that all parse correctly:
 * - `"AL, BA, CB1-5, CF31-48, 61-64, NP2, 4, 7-8, 22- 25"` (commas, hyphen with spaces)
 * - `"DG KA1-26, 29+ LL32-78"` (whitespace between tokens, `+` suffix)
 * - `"AL:BA:BS:CF:LA1-9:PE26-29:PE38+:WD"` (colon separator)
 */
export function expandPostcodeInput(input: string): ParseResult {
  const postcodes: string[] = []
  const warnings: string[] = []
  let currentPrefix = ''

  // Collapse whitespace around hyphens so ranges like "22 - 25" match the regex.
  const normalised = input.replace(/\s*-\s*/g, '-')

  // Split on commas, colons, or any whitespace.
  const tokens = normalised
    .split(/[,:\s]+/)
    .map((t) => t.trim().toUpperCase())
    .filter((t) => t.length > 0)

  for (const token of tokens) {
    let match: RegExpMatchArray | null

    if ((match = token.match(PREFIX_RANGE))) {
      // e.g. G1-62, KA1-18
      currentPrefix = match[1]!
      let start = parseInt(match[2]!, 10)
      let end = parseInt(match[3]!, 10)

      if (start > end) {
        warnings.push(`Range ${token} was inverted, auto-corrected to ${currentPrefix}${end}-${start}`)
        ;[start, end] = [end, start]
      }

      const size = end - start + 1
      if (size > MAX_RANGE_SIZE) {
        warnings.push(`Range ${token} exceeds maximum of ${MAX_RANGE_SIZE} postcodes, skipped`)
        continue
      }

      for (let i = start; i <= end; i++) {
        postcodes.push(`${currentPrefix}${i}`)
      }
    } else if ((match = token.match(PREFIX_OPEN_RANGE))) {
      // e.g. KA29+, PE38+
      currentPrefix = match[1]!
      const start = parseInt(match[2]!, 10)
      const end = OPEN_RANGE_END
      if (start > end) {
        warnings.push(`Open range ${token} starts beyond the cap (${OPEN_RANGE_END}), skipped`)
        continue
      }
      const size = end - start + 1
      if (size > MAX_RANGE_SIZE) {
        warnings.push(`Open range ${token} exceeds maximum of ${MAX_RANGE_SIZE} postcodes, skipped`)
        continue
      }
      for (let i = start; i <= end; i++) {
        postcodes.push(`${currentPrefix}${i}`)
      }
    } else if ((match = token.match(BARE_RANGE))) {
      // e.g. 64-82 (uses current prefix)
      let start = parseInt(match[1]!, 10)
      let end = parseInt(match[2]!, 10)

      if (start > end) {
        const display = currentPrefix ? `${currentPrefix}${token}` : token
        warnings.push(`Range ${display} was inverted, auto-corrected`)
        ;[start, end] = [end, start]
      }

      const size = end - start + 1
      if (size > MAX_RANGE_SIZE) {
        const display = currentPrefix ? `${currentPrefix}${start}-${end}` : token
        warnings.push(`Range ${display} exceeds maximum of ${MAX_RANGE_SIZE} postcodes, skipped`)
        continue
      }

      for (let i = start; i <= end; i++) {
        postcodes.push(`${currentPrefix}${i}`)
      }
    } else if ((match = token.match(BARE_OPEN_RANGE))) {
      // e.g. 29+ (uses current prefix → e.g. KA29-KA99)
      if (!currentPrefix) {
        warnings.push(`Open range ${token} has no prefix to inherit, skipped`)
        continue
      }
      const start = parseInt(match[1]!, 10)
      const end = OPEN_RANGE_END
      if (start > end) {
        warnings.push(`Open range ${currentPrefix}${token} starts beyond the cap (${OPEN_RANGE_END}), skipped`)
        continue
      }
      const size = end - start + 1
      if (size > MAX_RANGE_SIZE) {
        warnings.push(`Open range ${currentPrefix}${start}+ exceeds maximum of ${MAX_RANGE_SIZE} postcodes, skipped`)
        continue
      }
      for (let i = start; i <= end; i++) {
        postcodes.push(`${currentPrefix}${i}`)
      }
    } else if ((match = token.match(PREFIX_SINGLE))) {
      // e.g. G90, KA1, CV1
      currentPrefix = match[1]!
      postcodes.push(token)
    } else if ((match = token.match(BARE_NUMBER))) {
      // e.g. 90 (uses current prefix)
      postcodes.push(`${currentPrefix}${token}`)
    } else if ((match = token.match(PREFIX_ONLY))) {
      // e.g. B (standalone area prefix)
      currentPrefix = match[1]!
      postcodes.push(token)
    } else {
      // Passthrough: e.g. SW1A, or anything else
      postcodes.push(token)
    }
  }

  // Deduplicate while preserving order
  const seen = new Set<string>()
  const unique = postcodes.filter((p) => {
    if (seen.has(p)) return false
    seen.add(p)
    return true
  })

  return { postcodes: unique, warnings }
}
